import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  CheckCircle2,
  History,
  Loader2,
  Mic,
  Radio,
  RefreshCw,
  Search,
  Shield,
  Volume2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  dispatchPublicAddressAudio,
  getPublicAddressHistory,
  getPublicAddressSites,
  PublicAddressHistoryRow,
  PublicAddressSite,
} from '../services/publicAddressApi';
import './PublicAddressPage.css';

const PRE_RECORDED = [
  { number: 1, label: 'Atraco' },
  { number: 2, label: 'Vigilancia constante' },
  { number: 3, label: 'Consumo' },
  { number: 4, label: 'Expendio' },
  { number: 5, label: 'Riña' },
];

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
}

function bestMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm'];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || '';
}

const PublicAddressPage: React.FC = () => {
  const { token } = useAuth();
  const [sites, setSites] = useState<PublicAddressSite[]>([]);
  const [history, setHistory] = useState<PublicAddressHistoryRow[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState('');
  const [loading, setLoading] = useState(true);
  const [detectingMicrophones, setDetectingMicrophones] = useState(false);
  const [sendingSound, setSendingSound] = useState<number | null>(null);
  const [recording, setRecording] = useState(false);
  const [preparingRecording, setPreparingRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [sendingRecording, setSendingRecording] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const stopRequestedRef = useRef(false);
  const discardRecordingRef = useRef(false);

  const selectedSite = sites.find((site) => site.id === selectedSiteId) ?? null;
  const filteredSites = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('es');
    if (!query) return sites;
    return sites.filter((site) =>
      `${site.name} ${site.address}`.toLocaleLowerCase('es').includes(query),
    );
  }, [search, sites]);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [sitesResult, historyResult] = await Promise.allSettled([
        getPublicAddressSites(token),
        getPublicAddressHistory(token),
      ]);

      if (sitesResult.status === 'fulfilled') {
        const siteRows = sitesResult.value;
        setSites(siteRows);
        setSelectedSiteId((current) =>
          current && siteRows.some((site) => site.id === current)
            ? current
            : siteRows[0]?.id ?? null,
        );
      } else {
        setSites([]);
        setSelectedSiteId(null);
        toast.error('No se pudieron cargar los Escudos Urbanos.');
      }

      if (historyResult.status === 'fulfilled') {
        setHistory(historyResult.value);
      } else {
        setHistory([]);
        toast.error('No se pudo cargar el historial de perifoneo.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!recording) {
      setRecordingSeconds(0);
      return;
    }
    const update = () => setRecordingSeconds(Math.max(1, Math.ceil((Date.now() - startedAtRef.current) / 1000)));
    update();
    const timer = window.setInterval(update, 250);
    return () => window.clearInterval(timer);
  }, [recording]);

  useEffect(() => () => {
    discardRecordingRef.current = true;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    streamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  const detectMicrophones = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Este navegador no permite capturar audio.');
      return;
    }
    setDetectingMicrophones(true);
    try {
      const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      permissionStream.getTracks().forEach((track) => track.stop());
      const devices = (await navigator.mediaDevices.enumerateDevices())
        .filter((device) => device.kind === 'audioinput');
      setMicrophones(devices);
      setSelectedMicrophone((current) =>
        devices.some((device) => device.deviceId === current)
          ? current
          : devices[0]?.deviceId ?? '',
      );
      if (devices.length === 0) toast.error('No se detectaron micrófonos.');
    } catch {
      toast.error('No fue posible acceder al micrófono. Revisa los permisos del navegador.');
    } finally {
      setDetectingMicrophones(false);
    }
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const sendRecordedAudio = async (blob: Blob, durationSeconds: number) => {
    if (!token || !selectedSiteId) return;
    setSendingRecording(true);
    try {
      await dispatchPublicAddressAudio({
        token,
        siteId: selectedSiteId,
        audioType: 'custom',
        audio: blob,
        durationSeconds,
      });
      toast.success(`Audio enviado a ${selectedSite?.name ?? 'Escudo Urbano'}.`);
      setHistory(await getPublicAddressHistory(token));
    } catch {
      toast.error('No se pudo enviar la grabación.');
    } finally {
      setSendingRecording(false);
    }
  };

  const startPushToTalk = async () => {
    if (recording || preparingRecording || sendingRecording) return;
    if (!selectedSiteId) {
      toast.error('Selecciona un Escudo Urbano.');
      return;
    }
    if (!selectedMicrophone) {
      toast.error('Selecciona y habilita un micrófono.');
      return;
    }
    setPreparingRecording(true);
    stopRequestedRef.current = false;
    discardRecordingRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: selectedMicrophone }, echoCancellation: true, noiseSuppression: true },
      });
      if (stopRequestedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const mimeType = bestMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const duration = Math.max(1, (Date.now() - startedAtRef.current) / 1000);
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
        recorderRef.current = null;
        stopStream();
        setRecording(false);
        if (!discardRecordingRef.current && blob.size > 0) void sendRecordedAudio(blob, duration);
      };
      startedAtRef.current = Date.now();
      recorder.start(250);
      setRecording(true);
    } catch {
      stopStream();
      toast.error('No se pudo iniciar la grabación.');
    } finally {
      setPreparingRecording(false);
    }
  };

  const stopPushToTalk = () => {
    stopRequestedRef.current = true;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  };

  const sendPreRecorded = async (soundNumber: number) => {
    if (!token || !selectedSiteId || sendingSound !== null) return;
    setSendingSound(soundNumber);
    try {
      await dispatchPublicAddressAudio({
        token,
        siteId: selectedSiteId,
        audioType: 'prerecorded',
        soundNumber,
        durationSeconds: 10,
      });
      toast.success(`Audio ${soundNumber} enviado a ${selectedSite?.name ?? 'Escudo Urbano'}.`);
      setHistory(await getPublicAddressHistory(token));
    } catch {
      toast.error(`No se pudo enviar el audio ${soundNumber}.`);
    } finally {
      setSendingSound(null);
    }
  };

  return (
    <main className="public-address-page">
      <header className="pa-header">
        <div>
          <div className="pa-eyebrow"><Radio size={15} /> Alertas</div>
          <h1>Perifoneo</h1>
          <p>Envía mensajes personalizados o pregrabados a los Escudos Urbanos.</p>
        </div>
        <button className="pa-secondary-button" onClick={() => void loadData()} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'pa-spin' : ''} /> Actualizar
        </button>
      </header>

      <div className="pa-layout">
        <aside className="pa-sites-card">
          <div className="pa-card-title">
            <span><Shield size={18} /> Escudos Urbanos</span>
            <small>{sites.length}</small>
          </div>
          <label className="pa-search">
            <Search size={16} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar escudo..." />
          </label>
          <div className="pa-sites-list">
            {loading ? (
              <div className="pa-empty"><Loader2 className="pa-spin" /> Cargando...</div>
            ) : filteredSites.length === 0 ? (
              <div className="pa-empty">No hay Escudos Urbanos con perifoneo.</div>
            ) : filteredSites.map((site) => {
              const isSelected = selectedSiteId === site.id;
              return (
              <button
                key={site.id}
                type="button"
                className={`pa-site-option ${isSelected ? 'active' : ''}`}
                aria-pressed={isSelected}
                onClick={() => setSelectedSiteId(site.id)}
              >
                <span className={`pa-site-icon ${site.online ? 'online' : ''}`}><Shield size={20} /></span>
                <span className="pa-site-copy">
                  <strong>{site.name || `Sitio #${site.id}`}</strong>
                  <small>{site.address?.trim() || 'Sin dirección'}</small>
                </span>
                <span className={`pa-status-dot ${site.online ? 'online' : ''}`} title={site.online ? 'En línea' : 'Sin conexión'} />
              </button>
              );
            })}
          </div>
        </aside>

        <section className="pa-content">
          <div className="pa-control-card">
            <div className="pa-card-title">
              <span><Mic size={18} /> Push to talk</span>
              {selectedSite && <small>Destino: {selectedSite.name}</small>}
            </div>
            <div className="pa-microphone-row">
              <label>
                <span>Micrófono del sistema</span>
                <select
                  value={selectedMicrophone}
                  onChange={(event) => setSelectedMicrophone(event.target.value)}
                  disabled={recording || microphones.length === 0}
                >
                  {microphones.length === 0 && <option value="">Habilita un micrófono</option>}
                  {microphones.map((microphone, index) => (
                    <option key={microphone.deviceId} value={microphone.deviceId}>
                      {microphone.label || `Micrófono ${index + 1}`}
                    </option>
                  ))}
                </select>
              </label>
              <button className="pa-secondary-button" onClick={() => void detectMicrophones()} disabled={detectingMicrophones || recording}>
                {detectingMicrophones ? <Loader2 size={16} className="pa-spin" /> : <RefreshCw size={16} />}
                Detectar
              </button>
            </div>

            <div className="pa-ptt-area">
              <button
                className={`pa-ptt-button ${recording ? 'recording' : ''}`}
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.currentTarget.setPointerCapture(event.pointerId);
                  void startPushToTalk();
                }}
                onPointerUp={stopPushToTalk}
                onPointerCancel={stopPushToTalk}
                onContextMenu={(event) => event.preventDefault()}
                disabled={!selectedSiteId || !selectedMicrophone || sendingRecording}
              >
                {preparingRecording || sendingRecording ? <Loader2 size={34} className="pa-spin" /> : <Mic size={34} />}
                <strong>{sendingRecording ? 'Enviando...' : recording ? `Transmitiendo ${recordingSeconds}s` : 'Mantén presionado para hablar'}</strong>
                <span>{recording ? 'Suelta para enviar' : 'El audio se enviará al soltar'}</span>
              </button>
            </div>
          </div>

          <div className="pa-control-card">
            <div className="pa-card-title">
              <span><Volume2 size={18} /> Audios pregrabados</span>
              <small>Selecciona una alerta del 1 al 5</small>
            </div>
            <div className="pa-sounds">
              {PRE_RECORDED.map((sound) => (
                <button
                  key={sound.number}
                  className="pa-sound-button"
                  onClick={() => void sendPreRecorded(sound.number)}
                  disabled={!selectedSiteId || sendingSound !== null}
                >
                  <span className="pa-sound-number">{sound.number}</span>
                  <span><strong>{sound.label}</strong><small>Transmitir ahora</small></span>
                  {sendingSound === sound.number ? <Loader2 size={18} className="pa-spin" /> : <Volume2 size={18} />}
                </button>
              ))}
            </div>
          </div>

          <div className="pa-history-card">
            <div className="pa-card-title">
              <span><History size={18} /> Historial de envíos</span>
              <small>Últimos {history.length} registros</small>
            </div>
            <div className="pa-table-wrap">
              <table>
                <thead><tr><th>Fecha</th><th>Escudo Urbano</th><th>Audio</th><th>Operador</th><th>Estado</th></tr></thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr><td colSpan={5} className="pa-table-empty">Aún no se han enviado alertas de perifoneo.</td></tr>
                  ) : history.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDate(row.createdAt)}</td>
                      <td>{row.siteName}</td>
                      <td>
                        {row.audioType === 'custom'
                          ? 'Personalizado'
                          : PRE_RECORDED.find((sound) => sound.number === row.soundNumber)?.label
                            ?? `Pregrabado ${row.soundNumber}`}
                      </td>
                      <td>{row.operator}</td>
                      <td>
                        <span className={`pa-state ${row.status}`}>
                          {row.status === 'queued' ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />}
                          {row.status === 'queued' ? 'Enviado' : 'Fallido'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default PublicAddressPage;
